import React, { useCallback, useMemo } from 'react';
import localeLookup from '../../config/locale';
import CheckList from '../CheckList';
import ModalBody from './ModalBody';
import ModalFilter from './ModalFilter';
import ModalFooter from './ModalFooter';
import ModalHeader from './ModalHeader';
import ModalWrapper from './ModalWrapper';
import EmptyState from '../EmptyState';
import Text from '../Text';
import SwitchCheckbox from '../SwitchCheckbox';
import {
  compareFalsy,
  compareLocal,
  searchString,
  sortBy,
} from '../../utils/helpers';
import isEqual from 'lodash/isEqual';
import BoxMessage from '../BoxMessage';
import Button from '../Button';
import Tooltip from '../Tooltip';
import { FILTER_INPUT_SEPARATOR } from '../../constants';

export default function CheckListModal({
  options,
  selectedOptionIds: selectedOptionIdsProp,
  renderSectioned,
  nestedOptions,
  title,
  onClose,
  confirmButtonText,
  confirmButtonKind,
  onFilterButtonClick,
  filterPlaceholder,
  filterButtonText,
  showFilterButton,
  emptyStateText,
  subtitle,
  showToggleAll,
  showFilter,
  infoText,
  disableConfirmOnNoSelection,
  onConfirm,
}) {
  const [filterString, setFilterString] = React.useState('');

  const [selectedOptionIds, setSelectedOptionIds] = React.useState(
    selectedOptionIdsProp || []
  );

  const getVisibleOptions = useCallback(
    (options, nested = false) => {
      if (!filterString || (renderSectioned && !nested)) {
        return sortBy(options, [
          (a, b) => compareLocal(a.title, b.title),
          (a, b) =>
            compareFalsy(
              selectedOptionIdsProp.includes(a.id),
              selectedOptionIdsProp.includes(b.id)
            ),
        ]);
      }

      const filterParts = filterString
        .split(FILTER_INPUT_SEPARATOR)
        .map((part) => part.trim())
        .filter((part) => part);

      const filteredOptions = options.filter((option) => {
        if (filterString.includes(FILTER_INPUT_SEPARATOR)) {
          return filterParts.some((part) => {
            if (option.searchString) {
              return searchString(option.searchString, part);
            }
            return (
              searchString(option.title, part) ||
              searchString(option.subtitle, part)
            );
          });
        } else {
          if (option.searchString) {
            const doesMatch = searchString(option.searchString, filterString);
            return doesMatch;
          }
          return (
            searchString(option.title, filterString) ||
            searchString(option.subtitle, filterString)
          );
        }
      });

      if (nestedOptions) return filteredOptions;

      return sortBy(filteredOptions, [
        (a, b) => compareLocal(a.title, b.title),
        (a, b) =>
          compareFalsy(
            selectedOptionIdsProp.includes(a.id),
            selectedOptionIdsProp.includes(b.id)
          ),
      ]);
    },
    [filterString, renderSectioned, nestedOptions, selectedOptionIdsProp]
  );

  const visibleOptions = useMemo(
    () => getVisibleOptions(options),
    [getVisibleOptions, options]
  );

  const termsResultCount = useMemo(() => {
    const counts = {};
    if (!filterString || !filterString.includes(FILTER_INPUT_SEPARATOR))
      return counts;

    const filterParts = filterString
      .split(FILTER_INPUT_SEPARATOR)
      .map((part) => part.trim())
      .filter((part) => part);

    const allOptions = renderSectioned
      ? options.flatMap((section) => section.options)
      : options;

    allOptions.forEach((option) => {
      filterParts.forEach((part) => {
        const matches = option.searchString
          ? searchString(option.searchString, part)
          : searchString(option.title, part) ||
            searchString(option.subtitle, part);
        if (matches) {
          counts[part] = (counts[part] || 0) + 1;
        }
      });
    });

    return counts;
  }, [filterString, options, renderSectioned]);
  const isAnyOptionsDisabled = options.some((option) => {
    if (renderSectioned) {
      return option.options.some((nestedOption) => nestedOption.disabled);
    }
    return option?.disabled;
  });
  const isAnyVisibleOptionsDisabled = visibleOptions.some(
    (option) => option?.disabled
  );
  const confirmDisabled = disableConfirmOnNoSelection
    ? selectedOptionIds.length === 0
    : false;
  const showEmptyState =
    options.length === 0 ||
    (filterString.length > 0 && visibleOptions.length === 0);

  const getAddedOptionIds = () => {
    if (nestedOptions) return [];
    // Find options that are selected in state but not in props
    const addedOptionIds = selectedOptionIds.filter(
      (id) => !selectedOptionIdsProp.includes(id)
    );
    return addedOptionIds;
  };

  const visibleOptionsCount = useMemo(() => {
    if (renderSectioned) {
      return visibleOptions.reduce((count, section) => {
        const visibleSectionOptions = getVisibleOptions(section.options, true);
        return count + visibleSectionOptions.length;
      }, 0);
    }
    return visibleOptions.length;
  }, [visibleOptions, renderSectioned, getVisibleOptions]);

  const getAddedOptionNames = () => {
    const addedOptionIds = getAddedOptionIds();
    if (renderSectioned) {
      const addedOptionNames = [];
      options.forEach((group) => {
        group.options.forEach((option) => {
          if (addedOptionIds.includes(option.id)) {
            addedOptionNames.push(option.title);
          }
        });
      });
      return addedOptionNames;
    } else {
      const addedOptionNames = options
        .filter((option) => addedOptionIds.includes(option.id))
        .map((option) => option.title);
      return addedOptionNames;
    }
  };

  const getRemovedOptionIds = () => {
    if (nestedOptions) return [];
    // Find options that are selected in props but not in state
    const removedOptionIds = selectedOptionIdsProp.filter(
      (id) => !selectedOptionIds.includes(id)
    );
    return removedOptionIds;
  };

  const getRemovedOptionNames = () => {
    const removedOptionIds = getRemovedOptionIds();

    if (renderSectioned) {
      const removedOptionNames = [];
      options.forEach((group) => {
        group.options.forEach((option) => {
          if (removedOptionIds.includes(option.id)) {
            removedOptionNames.push(option.title);
          }
        });
      });
      return removedOptionNames;
    } else {
      const removedOptionNames = options
        .filter((option) => removedOptionIds.includes(option.id))
        .map((option) => option.title);
      return removedOptionNames;
    }
  };

  const isAllOptionsSelected = () => {
    if (renderSectioned) {
      return options.every((group) =>
        group.options.every((option) => selectedOptionIds.includes(option.id))
      );
    } else {
      return options.every((option) => selectedOptionIds.includes(option.id));
    }
  };

  const onClickDeselectAll = () => {
    if (renderSectioned) {
      if (filterString) {
        // Only deselect visible options that are not disabled
        const optionIdsToDeselect = options.reduce((acc, group) => {
          return [
            ...acc,
            ...getVisibleOptions(group.options, true).reduce((acc, option) => {
              // Filter out disabled options from deselection, as they cannot be selected in the first place
              // Also filter out options that are not currently selected, to avoid unnecessary state updates
              const isOptionSelected = selectedOptionIds.includes(option.id);
              if (option?.disabled || !isOptionSelected) {
                return acc;
              }
              return [...acc, option.id];
            }, []),
          ];
        }, []);
        // Deselect all visible options by removing them from selectedOptionIds
        setSelectedOptionIds((prevSelectedOptionIds) =>
          prevSelectedOptionIds.filter(
            (id) => !optionIdsToDeselect.includes(id)
          )
        );
      } else {
        // Deselect all options except disabled ones
        setSelectedOptionIds((prevSelectedOptionIds) => {
          // We want to deselect all options, but we need to keep disabled options selected, because they cannot be deselected
          const newOptionIds = prevSelectedOptionIds.filter((id) => {
            const option = options
              .flatMap((group) => group.options)
              .find((option) => option.id === id);
            return option && option?.disabled;
          });
          return newOptionIds;
        });
      }
    } else {
      if (filterString) {
        // Only deselect visible options that are not disabled
        const visibleOptions = getVisibleOptions(options);
        setSelectedOptionIds((prevSelectedOptionIds) =>
          prevSelectedOptionIds.filter((id) => {
            const matchedOption = options.find((o) => o.id === id);
            if (matchedOption && matchedOption.disabled) {
              return true;
            }
            if (
              visibleOptions.some((visibleOption) => visibleOption.id === id)
            ) {
              return false;
            }
            return true;
          })
        );
      } else {
        // Deselect all options except disabled ones
        setSelectedOptionIds((prevSelectedOptionIds) => {
          const newOptionIds = prevSelectedOptionIds.filter((id) => {
            const option = options.find((option) => option.id === id);
            return option && option?.disabled;
          });
          return newOptionIds;
        });
      }
    }
  };

  const onClickSelectAll = () => {
    if (renderSectioned) {
      // If filter is applied, only select visible options that are not disabled
      if (filterString) {
        const optionIds = options.reduce((acc, group) => {
          return [
            ...acc,
            ...getVisibleOptions(group.options, true).reduce((acc, option) => {
              if (option?.disabled) {
                return acc;
              }
              return [...acc, option.id];
            }, []),
          ];
        }, []);
        setSelectedOptionIds((prevSelectedOptionIds) => [
          ...new Set([...prevSelectedOptionIds, ...optionIds]),
        ]);
      } else {
        // If no filter, select all options except disabled ones
        const optionIds = options.reduce((acc, group) => {
          return [
            ...acc,
            ...getVisibleOptions(group.options, true).reduce((acc, option) => {
              if (option?.disabled) {
                return acc;
              }
              return [...acc, option.id];
            }, []),
          ];
        }, []);
        setSelectedOptionIds((prevSelectedOptionIds) => [
          ...new Set([...prevSelectedOptionIds, ...optionIds]),
        ]);
      }
    } else {
      // If filter is applied, only select visible options that are not disabled
      const visibleOptions = getVisibleOptions(options);
      if (filterString) {
        setSelectedOptionIds((prevSelectedOptionIds) => [
          ...new Set([
            ...prevSelectedOptionIds,
            ...visibleOptions.reduce((acc, option) => {
              if (option?.disabled) {
                return acc;
              }
              return [...acc, option.id];
            }, []),
          ]),
        ]);
      } else {
        // If no filter, select all options except disabled ones
        setSelectedOptionIds((prevSelectedOptionIds) => {
          return [
            ...new Set([
              ...prevSelectedOptionIds,
              ...visibleOptions.reduce((acc, option) => {
                if (option?.disabled) {
                  return acc;
                }
                return [...acc, option.id];
              }, []),
            ]),
          ];
        });
      }
    }
  };

  // TODO: Not used anymore. Safe to remove
  const onClickToggleAllOptions = (e) => {
    const { checked } = e.target;
    if (checked) {
      if (renderSectioned) {
        const optionIds = options.reduce((acc, group) => {
          return [
            ...acc,
            ...getVisibleOptions(group.options, true).map(
              (option) => option.id
            ),
          ];
        }, []);
        setSelectedOptionIds(optionIds);
      } else {
        setSelectedOptionIds(options.map((option) => option.id));
      }
    } else {
      setSelectedOptionIds([]);
    }
  };

  const handleConfirm = () => {
    onConfirm(selectedOptionIds, options);
  };

  const onFilterChange = (e) => {
    setFilterString(e.target.value);
  };

  const onToggleOption = (id, nestedOptionId) => {
    if (nestedOptions) {
      if (nestedOptionId) {
        setSelectedOptionIds((prevSelectedOptionIds) => ({
          ...prevSelectedOptionIds,
          [id]: prevSelectedOptionIds[id]?.includes(nestedOptionId)
            ? prevSelectedOptionIds[id].filter(
                (selectedId) => selectedId !== nestedOptionId
              )
            : [
                ...(prevSelectedOptionIds[id] ? prevSelectedOptionIds[id] : []),
                nestedOptionId,
              ],
        }));
      } else {
        const allOptionIds = options
          .find((option) => option.id === id)
          .options.filter((option) => !option?.disabled)
          .map((option) => option.value);

        const isAllNestedOptionsSelected = allOptionIds.every((optionId) =>
          selectedOptionIds[id]?.includes(optionId)
        );
        setSelectedOptionIds((prevSelectedOptionIds) => ({
          ...prevSelectedOptionIds,
          [id]: isAllNestedOptionsSelected ? [] : allOptionIds,
        }));
      }
    } else {
      setSelectedOptionIds((prevSelectedOptionIds) =>
        prevSelectedOptionIds.includes(id)
          ? prevSelectedOptionIds.filter((selectedId) => selectedId !== id)
          : [...prevSelectedOptionIds, id]
      );
    }
  };

  const renderAddedAndRemovedCount = () => {
    const addedOptionIds = getAddedOptionIds();
    const removedOptionIds = getRemovedOptionIds();
    if (addedOptionIds.length === 0 && removedOptionIds.length === 0) {
      return null;
    }
    const addedOptionNames = getAddedOptionNames();
    return (
      <>
        <Tooltip tooltip={addedOptionNames.join(', \n')}>
          <Text as="span" bold color="green">
            {addedOptionIds.length > 0 &&
              `${addedOptionIds.length} ${localeLookup('translations.added')}`}
            {addedOptionIds.length > 0 && removedOptionIds.length > 0 && ' · '}
          </Text>
        </Tooltip>
        <Tooltip tooltip={getRemovedOptionNames().join(', \n')}>
          <Text as="span" bold color="red">
            {removedOptionIds.length > 0 &&
              `${removedOptionIds.length} ${localeLookup(
                'translations.removed'
              )}`}
          </Text>
        </Tooltip>
      </>
    );
  };

  const renderSections = () => {
    return options.map((section, i) => {
      const visibleOptions = getVisibleOptions(section.options, true);
      if (visibleOptions.length === 0) return null;
      return (
        <React.Fragment key={i}>
          <h2 className="e-label-upper modal-content__body-title">
            {section.title}
          </h2>
          <CheckList
            onChange={onToggleOption}
            options={visibleOptions}
            selectedOptionIds={selectedOptionIds}
          />
        </React.Fragment>
      );
    });
  };

  return (
    <ModalWrapper>
      <ModalHeader onClose={onClose} subtitle={subtitle} title={title} />
      {infoText && (
        <BoxMessage icon="info-circle" spacing="md">
          {infoText}
        </BoxMessage>
      )}
      {showFilter !== false && (
        <ModalFilter
          buttonIcon="plus-circle"
          buttonText={filterButtonText}
          onButtonClick={() => onFilterButtonClick({ filterString })}
          onChange={onFilterChange}
          placeholder={
            filterPlaceholder || `${localeLookup('translations.Search')}...`
          }
          showButton={showFilterButton}
          showClearButton
          termsResultCount={termsResultCount}
          useChipInput
          value={filterString}
        />
      )}
      {showToggleAll && !isAnyVisibleOptionsDisabled && (
        <div className="checklist-header">
          <Text>{localeLookup('translations.Select all')}</Text>
          <SwitchCheckbox
            isChecked={isAllOptionsSelected()}
            onChange={onClickToggleAllOptions}
          ></SwitchCheckbox>
        </div>
      )}
      {!nestedOptions && (
        <div className="checklist-modal__bulk-selection-buttons">
          <Button className="" kind="link-style" onClick={onClickSelectAll}>
            {filterString.length > 0
              ? `${localeLookup('translations.Select all matches')} ${
                  isAnyOptionsDisabled ? '' : `(${visibleOptionsCount})`
                }`
              : localeLookup('translations.Select all')}
          </Button>
          ·
          <Button className="" kind="link-style" onClick={onClickDeselectAll}>
            {filterString.length > 0
              ? `${localeLookup('translations.Deselect all matches')} ${
                  isAnyOptionsDisabled ? '' : `(${visibleOptionsCount})`
                }`
              : localeLookup('translations.Deselect all')}
          </Button>
        </div>
      )}
      <ModalBody deep>
        {showEmptyState ? (
          <EmptyState body={emptyStateText} />
        ) : renderSectioned ? (
          renderSections()
        ) : (
          <CheckList
            nestedOptions={nestedOptions}
            onChange={onToggleOption}
            options={visibleOptions}
            selectedOptionIds={selectedOptionIds}
          />
        )}
      </ModalBody>
      <ModalFooter
        confirmButtonKind={confirmButtonKind}
        confirmButtonText={confirmButtonText}
        confirmDisabled={confirmDisabled}
        onCancelClick={onClose}
        onConfirmClick={handleConfirm}
        renderLeft={renderAddedAndRemovedCount}
      />
    </ModalWrapper>
  );
}
